import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ChevronDown, Sparkles } from "lucide-react";
import { Toaster } from "sonner";

import SEO from "@/components/SEO";
import { useLenis } from "@/components/layout/SmoothScrollProvider";
import { Button } from "@/components/ui/button";

import PreuvesSection from "@/components/sections/PreuvesSection";
import VosEnjeuxSection from "@/components/sections/VosEnjeuxSection";
import EngagementSection from "@/components/sections/EngagementSection";
import ExpertisesSection from "@/components/sections/ExpertisesSection";
import WhyUsSection from "@/components/sections/WhyUsSection";
import EnvironnementsSection from "@/components/sections/EnvironnementsSection";
import ReferencesSection from "@/components/sections/ReferencesSection";
import CareersSection from "@/components/sections/CareersSection";
import ContactSection from "@/components/sections/ContactSection";

const DEFAULT_SECTION_SCROLL_OFFSET = -80;

const getSectionScrollOffset = (element) => {
  if (typeof window === "undefined" || !element) {
    return DEFAULT_SECTION_SCROLL_OFFSET;
  }

  const scrollMarginTop = window.getComputedStyle(element).scrollMarginTop;
  const parsedMarginTop = Number.parseFloat(scrollMarginTop);

  if (!Number.isFinite(parsedMarginTop) || parsedMarginTop <= 0) {
    return DEFAULT_SECTION_SCROLL_OFFSET;
  }

  return -parsedMarginTop;
};



const HeroSection = () => {
  const lenisRef = useLenis();
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"],
  });
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.5], [1, 1.05]);
  const y = useTransform(scrollYProgress, [0, 0.5], [0, 50]);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      if (lenisRef?.current) {
        lenisRef.current.scrollTo(element, { offset: getSectionScrollOffset(element) });
      } else {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  const scrollToNext = () => {
    const element = document.getElementById("preuves");
    if (element) {
      if (lenisRef?.current) {
        lenisRef.current.scrollTo(element, { offset: -100 });
      } else {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  return (
    <section ref={containerRef} className="relative min-h-[100dvh] flex flex-col justify-center hero-bg overflow-hidden pt-28 pb-16">
      {/* Premium Background with Human Presence */}
      <motion.div style={{ scale }} className="absolute inset-0 z-0">
        {/* Subtle developer silhouette - right side */}
        <div className="absolute right-0 top-0 w-full md:w-3/5 h-full">
          <img
            src="https://images.unsplash.com/photo-1536148935331-408321065b18?w=1200&q=75"
            alt=""
            className="w-full h-full object-cover opacity-[0.18]"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#070B1A] via-[#070B1A]/90 to-[#070B1A]/40" />
        </div>
        
        {/* Multi-layer gradient overlay for premium depth */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#070B1A]/40 via-transparent to-[#070B1A]" />
      </motion.div>

      {/* Ambient glow effects - Plus "Enterprise" */}
      <div className="absolute -top-32 -left-32 w-[800px] h-[800px] rounded-full hero-glow-blue opacity-30 mix-blend-screen" />
      <div className="absolute bottom-0 right-0 w-[700px] h-[700px] rounded-full hero-glow-violet opacity-30 mix-blend-screen" />
      
      {/* Subtle grid pattern overlay */}
      <div 
        className="absolute inset-0 opacity-[0.025]"
        style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)`,
          backgroundSize: '80px 80px'
        }}
      />

      <motion.div style={{ opacity, y }} className="lenvia-container relative z-10 w-full">
        <div className="max-w-[750px] lg:max-w-[880px] xl:max-w-[950px] flex flex-col gap-6 lg:gap-8">
          {/* Premium Tag */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0 }}
            className="flex"
          >
            <span className="inline-flex items-center gap-2.5 px-4 py-2.5 rounded-full bg-white/[0.02] border border-white/[0.05] backdrop-blur-md text-xs font-semibold text-slate-300 tracking-wide shadow-[0_0_15px_-3px_rgba(255,255,255,0.05)]">
              <Sparkles className="w-3.5 h-3.5 text-lenvia-blue" />
              Expertise Mobile & IA pour systèmes critiques
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-heading fluid-h1 font-extrabold text-[#F8FAFC]"
            data-testid="hero-headline"
          >
            Nous intervenons sur des{" "}
            <span className="gradient-text">systèmes critiques</span>{" "}
            où l'échec n'est pas une option.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="fluid-p leading-[1.6] text-slate-300 max-w-2xl font-normal"
            data-testid="hero-subtext"
          >
            Sécurisez vos déploiements et accélérez votre time-to-market. Nous concevons des architectures logicielles résilientes capables de soutenir vos enjeux d'échelle.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-2 flex flex-col sm:flex-row items-start sm:items-center gap-5 sm:gap-6 lg:gap-8"
          >
            <Button
              className="btn-linear text-base sm:text-lg group px-8 py-6 sm:py-5 w-full sm:w-auto shrink-0"
              onClick={() => scrollToSection("contact")}
              data-testid="hero-cta-contact"
            >
              Échanger sur votre projet
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
            </Button>

            <p className="fluid-micro text-slate-400/60 font-medium tracking-wide">
              Échange expert • Sans engagement • Sous 24h
            </p>
          </motion.div>

        </div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 0.5 }}
        className="absolute bottom-6 md:bottom-10 left-1/2 -translate-x-1/2 z-10 hidden sm:flex hide-on-short flex-col items-center gap-2 pointer-events-none"
      >
        <button
          onClick={scrollToNext}
          className="flex flex-col items-center gap-2 text-slate-500 hover:text-slate-300 transition-colors cursor-pointer group pointer-events-auto"
        >
          <span className="text-[0.65rem] font-semibold tracking-[0.15em] uppercase opacity-60 group-hover:opacity-100 transition-opacity">Découvrir</span>
          <ChevronDown className="w-4 h-4 animate-bounce opacity-60 group-hover:opacity-100" />
        </button>
      </motion.div>
    </section>
  );
};

const HomePage = () => {
  return (
    <>
      <SEO
        title="Lenvia | Partenaire Technologique Premium - Mobile & IA"
        description="Transformez vos enjeux métiers avec Lenvia. Expertise sur mesure en Mobile Engineering, IA et Delivery pour les systèmes critiques des CTO et DSI."
      />

      <HeroSection />
      
      <PreuvesSection />
      <VosEnjeuxSection />
      <EngagementSection />
      <ExpertisesSection />
      <WhyUsSection />
      <EnvironnementsSection />
      <ReferencesSection />
      <CareersSection />
      <ContactSection />
      <Toaster
        position="top-right"
        richColors
        theme="dark"
        toastOptions={{
          style: {
            background: "#0F1423",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            color: "#F8FAFC",
          },
        }}
      />
    </>
  );
};

export default HomePage;
